import { useEffect, useState, type ReactNode } from 'react';

const MIN_WIDTH = import.meta.env.DEV ? 360 : 1024;

interface ViewportGateProps {
  children: ReactNode;
}

/**
 * Desktop-only gate.
 *
 * Per brief Q16: admin requires ≥1024px outside local dev. In dev we relax
 * this so Codex's side-by-side iframe review page can render the admin panel
 * next to the phone-sized app.
 */
export function ViewportGate({ children }: ViewportGateProps) {
  const [tooNarrow, setTooNarrow] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < MIN_WIDTH,
  );

  useEffect(() => {
    function onResize() {
      setTooNarrow(window.innerWidth < MIN_WIDTH);
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (tooNarrow) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-background p-8">
        <div className="max-w-md text-center">
          <p className="font-barlow text-3xl font-semibold uppercase tracking-wide">
            Admin requires desktop
          </p>
          <p className="mt-3 text-sm text-muted-foreground">
            This panel is built for screens at least {MIN_WIDTH}px wide. Please open it on a
            desktop browser.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
